import React from "react";
import { useEffect, useRef } from "react"
import PropTypes from "prop-types";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import "./DropDownList.scss";

/**
 * Dropdown list
 * @module
 * @param {array} data - All data for options
 * @param {string} textField - Text field
 * @param {object} [value] - Current value
 * @param {func} onChange - Function invoked on value change
 * @param {func} [onOpen] - Function invoked on dropdown list open
 * @param {bool} [disabled] - Is input readonly?
 * @param {bool} [required] - Is input required?
 * @param {string} name - Name
 * @param {string} [validationMessage] - Validation message if field is invalid
 * @param {number} [tabIndex] - Tabindex
 * @param {string} [className] - Class name for dropdown list
 * @param {func} [onBlur] - Function invoked on dropdown list blur
 * @param {object} [popupSettings] - Popup settings
 * @param {object} [defaultItem] - Default item
 * @param {bool} [notSetValue] - Not set value if is not needed
 * @param {func} [valueRender] - Used to override the default render of the value.
 * @param {func} [itemRender] - Used to override the default render of the data item.
 */
const DropDownListComponent = (props) => {
  //tests
  const dropDownRef = useRef(null);
  useEffect(() => {
    if (dropDownRef.current && dropDownRef.current.element && props.name) {
      dropDownRef.current.element.setAttribute('data-testid', props.name);
    }
  }, [props.name])

  const getValue = () => {
    if (props.notSetValue) {
      return;
    } else if (!props.value && props.required) {
      const { data } = props;
      const dataItem = data.length > 0 ? data[0] : null;
      if (dataItem) { props.onChange(props.name, dataItem); }
    }

    return props.value;
  };

  const onOpen = (e) => {
    if (props.onOpen) {
      props.onOpen(props.name, e.target.value);
    }
  };

  const onBlur = () => {
    if (props.onBlur) {
      props.onBlur();
    }
  };

  return (
    <DropDownList
      className={`form-input dropdownlist ${props.disabled ? "disabled-field" : props.required ? "required-field" : (props.recommended ? "recommended-field" : "default-field")} ${props.className || ""}`}
      data={props.data}
      textField={props.textField}
      dataItemKey={props.dataItemKey || props.textField}
      value={getValue()}
      onChange={(e) => props.onChange(props.name, e.target.value)}
      required={props.required}
      validationMessage={props.validationMessage || "Povinné"}
      disabled={props.disabled}
      tabIndex={props.tabIndex || 0}
      onOpen={onOpen}
      onBlur={onBlur}
      popupSettings={props.popupSettings}
      defaultItem={props.defaultItem}
      valueRender={props.valueRender}
      itemRender={props.itemRender}
      ref={dropDownRef}
    />
  );
};

DropDownListComponent.propTypes = {
  data: PropTypes.array.isRequired,
  textField: PropTypes.string.isRequired,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onOpen: PropTypes.func,
  required: PropTypes.bool,
  name: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]).isRequired,
  validationMessage: PropTypes.string,
  disabled: PropTypes.bool,
  tabIndex: PropTypes.number,
  className: PropTypes.string,
  onBlur: PropTypes.func,
  popupSettings: PropTypes.object,
  defaultItem: PropTypes.object,
  notSetValue: PropTypes.bool,
  valueRender: PropTypes.func,
  itemRender: PropTypes.func,
};

export default DropDownListComponent;
